import React, { createContext, useContext, ReactNode } from 'react';
import { useChat } from '../hooks/useChat.final';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { UseChatReturn } from '../types/hooks';
import { ChatErrorBoundary } from '../components/chat/ChatErrorBoundary';

const ChatContext = createContext<UseChatReturn | null>(null);

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const chatState = useChat();
  const errorHandler = useErrorHandler();
  
  const enhancedChatState: UseChatReturn = {
    ...chatState,
    ...errorHandler
  };
  
  return (
    <ChatErrorBoundary>
      <ChatContext.Provider value={enhancedChatState}>
        {children}
      </ChatContext.Provider>
    </ChatErrorBoundary>
  );
};

export const useChatContext = (): UseChatReturn => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

// Individual hooks for specific functionality
export const useChatSessions = () => {
  const { sessions, currentSession, currentSessionId, loadingSessions, pendingNewSession, hasMoreSessions, isLoadingMore, startNewSession, selectSession, deleteSession, loadMoreSessions } = useChatContext();
  return { sessions, currentSession, currentSessionId, loadingSessions, pendingNewSession, hasMoreSessions, isLoadingMore, startNewSession, selectSession, deleteSession, loadMoreSessions };
};

export const useChatMessages = () => {
  const { messages, loadingMessages, sendMessage, isTyping, statusMessage } = useChatContext();
  return { messages, loadingMessages, sendMessage, isTyping, statusMessage };
};

export const useChatConnection = () => {
  const { isConnected } = useChatContext();
  return { isConnected };
};

export const useChatErrors = () => {
  const { errors, hasErrors, latestError, addError, removeError, clearErrors } = useChatContext();
  return { errors, hasErrors, latestError, addError, removeError, clearErrors };
};

// Development helper for testing error handling
export const useChatErrorTesting = () => {
  const { addError } = useChatErrors();
  
  const triggerTestError = (type: 'connection' | 'message' | 'session' | 'auth') => {
    const messages = {
      connection: 'Lost connection to the server. Please check your internet connection.',
      message: 'Failed to send message. The message could not be delivered.',
      session: 'Unable to load chat session. Session data may be corrupted.',
      auth: 'Authentication failed. Please log in again.'
    };
    
    addError(type, messages[type], { 
      testError: true, 
      timestamp: new Date().toISOString() 
    });
  };

  return { triggerTestError };
};
